/* eslint-disable no-restricted-imports */
import {
  alpha,
  FormControlLabel,
  FormControlLabelProps,
  RadioGroup as MuiRadioGroup,
  RadioGroupProps as MuiRadioGroupProps,
  Radio,
  radioClasses,
  styled,
  Typography,
} from '@mui/material';
import {isString} from 'lodash';
import {ComponentType, forwardRef} from 'react';
import {SetOptional} from 'type-fest';

export type RadioGroupOption = SetOptional<FormControlLabelProps, 'control'>;

type Variant = 'default' | 'dropdown-options' | 'inline-dark';

export type RadioGroupProps = MuiRadioGroupProps & {
  options?: RadioGroupOption[];
  variant?: Variant;
  disabled?: boolean;
};

const SDropdownOptionsRadioGroup = styled(MuiRadioGroup)(({theme}) => ({
  [`&.MuiFormGroup-root:not(.MuiFormGroup-row) .MuiFormControlLabel-root`]: {
    padding: theme.spacing(2, 3),
    border: 'none',
    borderRadius: 0,
    color: theme.palette.text.primary,

    '&.Mui-disabled': {
      color: theme.palette.text.disabled,
    },

    [`& .${radioClasses.root}`]: {
      color: theme.palette.text.primary,

      '&.Mui-disabled': {
        color: theme.palette.text.disabled,
      },
    },

    [`& .${radioClasses.checked}`]: {
      color: theme.palette.text.primary,
    },

    '&:last-child': {
      borderBottom: 'none',
      borderRadius: 0,
    },

    '&:first-of-type': {
      borderRadius: 0,
    },

    '&:hover:not(.Mui-disabled)': {
      backgroundColor: alpha(theme.palette.primary.main, theme.palette.action.hoverOpacity),
    },
  },
}));

const SInlineDarkRadioGroup = styled(MuiRadioGroup)(({theme}) => ({
  gap: theme.spacing(6),

  [`&.MuiFormGroup-root.MuiFormGroup-row .MuiFormControlLabel-root`]: {
    padding: theme.spacing(2, 3),
    border: 'none',
    borderRadius: theme.shape.borderRadiusRegular,
    color: theme.palette.text.primary,

    '&.Mui-disabled': {
      color: theme.palette.text.disabled,
    },

    [`& .${radioClasses.root}`]: {
      color: theme.palette.text.primary,

      '&.Mui-disabled': {
        color: theme.palette.text.disabled,
      },
    },

    [`& .${radioClasses.checked}`]: {
      color: theme.palette.text.primary,
    },

    '&:hover:not(.Mui-disabled)': {
      backgroundColor: alpha(theme.palette.primary.main, theme.palette.action.hoverOpacity),
    },
  },
}));

const variantMap: Record<Variant, ComponentType<RadioGroupProps>> = {
  default: MuiRadioGroup,
  'dropdown-options': SDropdownOptionsRadioGroup,
  'inline-dark': SInlineDarkRadioGroup,
};

export const RadioGroup = forwardRef<unknown, RadioGroupProps>(
  ({options, children, variant = 'default', disabled, ...restProps}, ref) => {
    const Component = variantMap[variant];

    return (
      <Component {...restProps} ref={ref}>
        {options?.map((option, index) => (
          <RadioOption key={index} disabled={disabled} control={<Radio />} {...option} />
        ))}
        {children}
      </Component>
    );
  }
);

export function RadioOption({label, ...props}: RadioGroupOption) {
  const labelContent = isString(label) ? <Typography variant='buttonSmall'>{label}</Typography> : label;

  return (
    <div data-radio-option-id={props.value}>
      <FormControlLabel {...props} control={<Radio />} label={labelContent} disableTypography />
    </div>
  );
}
